// web/src/utils/FavoriteButton.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";

const API = {
  list:   "/api/me/favorites",
  add:    "/api/me/favorites",
  remove: "/api/me/favorites",
};

export default function FavoriteButton({ banId, ban_id, label, compact = false }) {
  const targetId = banId || ban_id || "";
  const { user } = useAuth();
  const [isFav, setIsFav] = useState(false);
  const [busy, setBusy] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(!!mq.matches);
    update();
    try { mq.addEventListener("change", update); } catch {}
    return () => { try { mq.removeEventListener("change", update); } catch {} };
  }, []);

  useEffect(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem("DICTIMMO_FAVS") || "[]");
      setIsFav(Array.isArray(cached) && targetId && cached.includes(targetId));
    } catch {}
  }, [targetId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user || !targetId) return;
      try {
        const r = await fetch(API.list, { credentials: "include" });
        if (!r.ok) return;
        const arr = await r.json();
        const ids = (Array.isArray(arr) ? arr : []).map(x => x?.ban_id).filter(Boolean);
        if (cancelled) return;
        sessionStorage.setItem("DICTIMMO_FAVS", JSON.stringify(ids));
        setIsFav(ids.includes(targetId));
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user, targetId]);

  const toggle = async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { mode: "login" } }));
      return;
    }
    if (!targetId || busy) return;
    setBusy(true);
    try {
      if (!isFav) {
        const r = await fetch(API.add, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ban_id: targetId, label }),
        });
        if (!r.ok) throw new Error(await r.text());
        setIsFav(true);
        try {
          const cached = JSON.parse(sessionStorage.getItem("DICTIMMO_FAVS") || "[]");
          const nx = Array.isArray(cached) ? Array.from(new Set([targetId, ...cached])).slice(0, 50) : [targetId];
          sessionStorage.setItem("DICTIMMO_FAVS", JSON.stringify(nx));
        } catch {}
      } else {
        const r = await fetch(API.remove, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ban_id: targetId }),
        });
        if (!r.ok) throw new Error(await r.text());
        setIsFav(false);
        try {
          const cached = JSON.parse(sessionStorage.getItem("DICTIMMO_FAVS") || "[]");
          const nx = (Array.isArray(cached) ? cached : []).filter(x => x !== targetId);
          sessionStorage.setItem("DICTIMMO_FAVS", JSON.stringify(nx));
        } catch {}
      }
    } catch (e) {
      console.error(e);
      alert("Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  // Shorter text on mobile to keep width reasonable
  const labelText = useMemo(() => {
    if (isFav) return "Favori";
    return isMobile || compact ? "Favori" : "Ajouter aux favoris";
  }, [isFav, isMobile, compact]);

  const style = {
    display: "inline-flex", alignItems: "center", gap: 10,
    padding: "14px 20px",
    fontSize: 16, fontWeight: 700,
    borderRadius: 14, border: "none",
    cursor: busy ? "not-allowed" : "pointer",
    color: "#fff",
    backgroundImage: isFav
      ? "linear-gradient(135deg, #f59e0b, #d97706)"
      : "linear-gradient(135deg, #fbbf24, #f59e0b)",
    boxShadow: "0 8px 18px rgba(245,158,11,0.25)",
    opacity: busy ? 0.8 : 1,
    WebkitTapHighlightColor: "transparent",
    transition: "transform .12s ease, box-shadow .12s ease, opacity .12s ease",
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy || !targetId}
      aria-pressed={isFav}
      data-cta
      title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
      style={style}
    >
      <span aria-hidden="true" style={{fontSize:18, lineHeight:1}}>
        {isFav ? "⭐" : "☆"}
      </span>
      <span>{labelText}</span>
    </button>
  );
}
